import React from 'react'
import { useT } from '@transifex/react'

import { Spinner } from 'src/components/ui/spinner'
import { cn } from 'src/lib/utils'
import { LOADING_DELAY_MS } from 'src/ui/loading/constants'
import { useLoadingDelay } from 'src/ui/loading/useLoadingDelay'

import type { SpinProps, SpinSize } from './props'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Spin` seam. `./index.tsx` is the dispatcher
 * that chooses between this and antd's own Spin; nothing else imports this
 * file, and nothing reaches it until someone flips the design toggle
 * (`src/design-mode`).
 *
 * The EXTERNAL API is antd's own `SpinProps`, so the 124 files that already
 * import `Spin` from `src/antd` switch with no edit at all.
 *
 * WHAT antd's Spin ACTUALLY IS, and what this repo uses of it. `Spin` is not
 * only a glyph: it is three things at once, and the counts below are this
 * repo's, taken from the 135 JSX tags that render it.
 *
 *   • an INDICATOR — `<Spin />`, `<Spin size="small" />`. 87 tags. The whole
 *     component is the spinner.
 *   • an OVERLAY — `<Spin spinning={loading}>{content}</Spin>`. 48 tags.
 *     antd calls this the nested pattern: the content stays mounted, dimmed
 *     and inert, with the spinner floating over it. Keeping the content
 *     mounted is the point — a list that blurs during a refetch does not
 *     lose its scroll position, and it is why `spinning` toggles in place
 *     rather than the call site swapping the subtree out.
 *   • a LABEL — `tip`. 4 tags, and every one of them is a NO-OP today: antd
 *     v5 renders `tip` only in the nested or fullscreen pattern (it dev-warns
 *     otherwise), and all four pass it on a self-closing `<Spin />`. This
 *     branch reproduces that exactly, warning and all, rather than quietly
 *     starting to render four strings nobody has seen since antd v4.
 *
 * Prop coverage, stated rather than assumed. Everything antd's Spin takes is
 * honoured here: `spinning`, `size`, `tip`, `indicator`, `delay`, `className`,
 * `rootClassName`, `wrapperClassName`, `style`, `children`, `fullscreen`, and
 * the `data-*` / aria passthrough. The static `Spin.setDefaultIndicator` is
 * honoured too — `./index.tsx` writes both antd's copy and this one, so a
 * caller cannot set a default that only half the app sees. No call site in
 * this repo uses `delay`, `fullscreen`, `wrapperClassName`, `rootClassName` or
 * `setDefaultIndicator` today; they are implemented so the seam is a drop-in
 * rather than a subset that fails the first time someone reaches for one.
 *
 * THE ONE DELIBERATE BEHAVIOUR CHANGE, toggle-on only: `delay` defaults to
 * `LOADING_DELAY_MS` instead of antd's `0`. That is the captain's ask — a
 * spinner that flashes for 80ms is exactly as bad as the skeleton wall that
 * did — and it is the reason this port addresses PERCEIVED speed rather than
 * only appearance. A call site that means "show it now" can still pass
 * `delay={0}`. `src/ui/loading/constants.ts` carries the number and the
 * reasoning.
 *
 * NO `.ant-spin*` CLASS NAMES ARE RE-EMITTED. antd is still installed on this
 * branch and its stylesheets are UNLAYERED, so they outrank every Tailwind
 * utility: `.ant-spin-dot` carries `position: absolute` and its own 20px
 * four-dot geometry, and `.ant-spin-container` carries the blur. Re-emitting
 * either would inherit antd's rules on top of this seam's. This is the
 * `.ant-btn` case, not the `.ant-space` one — so the seam renders
 * `data-slot="spin"` and its relatives instead. Every live stylesheet that
 * selected `.ant-spin-dot` on a seam Spin grew a selector for the seam's
 * rendering beside it, so both renderings stay covered; AGENTS.md's Spin entry
 * lists them.
 *
 * SIZES come from antd's own tokens, not from shadcn's `size-4` default:
 * `small` is 14px, `default` 20px, `large` 32px — the dot diameters antd
 * draws. Matching them is what keeps a `<Spin size="small" />` sitting inside
 * a table cell the same height it was.
 * ------------------------------------------------------------------------- */

/** antd's `dotSize` per size token (antd 5.15 `Spin` component tokens). */
const SIZE_CLASS: Record<SpinSize, string> = {
  small: 'text-[14px]',
  default: 'text-[20px]',
  large: 'text-[32px]',
}

let seamDefaultIndicator: React.ReactNode = null

/**
 * The seam's half of antd's `Spin.setDefaultIndicator`. `./index.tsx` calls
 * this and antd's together, so the two branches cannot disagree about what the
 * app's default spinner is.
 */
export const setSeamDefaultIndicator = (indicator: React.ReactNode) => {
  seamDefaultIndicator = indicator
}

export const Spin = (props: SpinProps) => {
  const t = useT()
  const {
    spinning: customSpinning = true,
    // The threshold, applied by DEFAULT rather than only on request. See the
    // header: this is the piece that addresses "feels slower than it is".
    delay = LOADING_DELAY_MS,
    className,
    rootClassName,
    size = 'default',
    tip,
    wrapperClassName,
    style,
    children,
    fullscreen = false,
    indicator,
    // `prefixCls` is antd's cssinjs hook and means nothing without antd's
    // stylesheet; swallow it so it cannot reach the DOM.
    prefixCls: _prefixCls,
    ...restProps
  } = props

  const delayElapsed = useLoadingDelay(customSpinning ? delay : 0)
  const spinning = !!customSpinning && delayElapsed

  const isNestedPattern = typeof children !== 'undefined' && !fullscreen
  // antd renders the standalone element either way and hides it with
  // `.ant-spin { display: none }` until `.ant-spin-spinning` is added, so a
  // `styled(Spin)` call site keeps its node, its ref and its `data-*` through
  // the whole cycle — including the delay window before the spinner is due.
  // Same here: hidden, not unmounted.
  const isStandaloneHidden = !isNestedPattern && !fullscreen && !spinning

  if (process.env.NODE_ENV !== 'production' && tip && !isNestedPattern) {
    // antd's own dev warning, kept verbatim in meaning: `tip` renders in the
    // nested and fullscreen patterns only, and four call sites in this repo
    // pass it on a bare `<Spin />` where it has never rendered.
    // eslint-disable-next-line no-console
    console.warn(
      'Warning: [antd: Spin] `tip` only work in nest or fullscreen pattern.'
    )
  }

  // antd resolves the indicator in this order and so does this: an explicit
  // `indicator` prop, then whatever `setDefaultIndicator` installed, then the
  // component's own. `indicator === null` means "draw nothing" — that is antd's
  // documented way to render an empty Spin, so it must not fall through to the
  // default.
  let indicatorNode: React.ReactNode
  if (indicator === null) {
    indicatorNode = null
  } else if (React.isValidElement(indicator)) {
    indicatorNode = indicator
  } else if (React.isValidElement(seamDefaultIndicator)) {
    indicatorNode = seamDefaultIndicator
  } else {
    indicatorNode = (
      <Spinner className={SIZE_CLASS[size]} label={t('Loading')} />
    )
  }

  const spinElement = (
    <div
      {...(restProps as React.HTMLAttributes<HTMLDivElement>)}
      data-slot="spin"
      data-size={size}
      style={style}
      className={cn(
        // THE COLOUR LIVES HERE, on the wrapper, and the glyph inherits it:
        // the icon seam's runtime sheet carries `.anticon { color: inherit }`
        // UNLAYERED, so a colour utility on the Spinner itself loses no matter
        // what its specificity says (`src/components/ui/spinner.tsx` says so,
        // browser-confirmed). `text-muted-foreground` is the design system's
        // secondary/muted foreground and is the app's ONE loading colour —
        // `src/ui/loading/LoadingWall.tsx` and
        // `./AntdDefaultIndicator.tsx` use the same token, so a spinner reads
        // the same grey on every surface rather than brand blue on some.
        'inline-flex flex-col items-center justify-center gap-2 text-muted-foreground',
        // The overlay in the nested pattern, mirroring antd's
        // `.ant-spin-nested-loading > div > .ant-spin`: pinned to the top of
        // the wrapper, full width, capped at 400px tall so the spinner stays
        // near the content on a very long list.
        isNestedPattern &&
          'absolute inset-x-0 top-0 z-[4] flex h-full max-h-[400px] w-full',
        // …and the whole-viewport cover in the fullscreen one.
        // The one surface that does NOT take the muted grey, and for a
        // contrast reason rather than a stylistic one: `fullscreen` draws the
        // spinner on antd's own black/45 scrim, where a mid-grey is barely
        // legible. antd paints this one white too. No call site in this repo
        // uses `fullscreen` today.
        fullscreen && 'fixed inset-0 z-[1000] flex bg-black/45 text-white',
        isStandaloneHidden && 'hidden',
        className,
        rootClassName
      )}
      aria-live="polite"
      aria-busy={spinning}
    >
      {indicatorNode}
      {tip && (isNestedPattern || fullscreen) ? (
        <div data-slot="spin-text" className="text-sm">
          {tip}
        </div>
      ) : null}
    </div>
  )

  if (isNestedPattern) {
    return (
      <div
        {...(restProps as React.HTMLAttributes<HTMLDivElement>)}
        data-slot="spin-nested"
        className={cn('relative clear-both', wrapperClassName)}
      >
        {spinning && spinElement}
        <div
          data-slot="spin-container"
          // antd's `.ant-spin-blur`: the content stays mounted and readable
          // through the overlay, but stops responding. Reproduced as
          // utilities rather than by re-emitting the class name.
          className={cn(
            spinning && 'pointer-events-none select-none opacity-50'
          )}
        >
          {children}
        </div>
      </div>
    )
  }

  if (fullscreen) return spinning ? spinElement : null

  return spinElement
}
